"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import AwesomeDebouncePromise from '../../_modules/awesome-debounce-promise.mjs';
import { useMembrane } from '../../contexts/use-integration-app.mjs';
import { stringifyElementKey } from './element-key.mjs';
import useSWR from 'swr';

const elementUpdateStateCache = /* @__PURE__ */ new Map();
function useElement(type, selector, accessorGenerator) {
  const integrationApp = useMembrane();
  const elementKey = stringifyElementKey({
    type,
    selector,
    token: integrationApp?.token
  });
  const elementState = elementUpdateStateCache.get(elementKey) ?? {
    updatedLocally: false,
    savingToServer: false,
    currentPutRequests: [],
    // Save current put requests to not update local state until all of them are done
    debouncedPut: AwesomeDebouncePromise(async (data) => {
      const requestId = Math.random();
      elementState.currentPutRequests.push(requestId);
      elementState.updatedLocally = false;
      elementState.savingToServer = true;
      try {
        const result = await accessor?.put(data);
        elementState.currentPutRequests = elementState.currentPutRequests.filter((id) => id !== requestId);
        if (!elementState.updatedLocally && elementState.currentPutRequests.length === 0) {
          elementState.savingToServer = false;
          await mutate(result, false);
        }
      } catch (e) {
        elementState.updatedLocally = true;
        throw e;
      } finally {
        elementState.savingToServer = false;
      }
    }, 500)
  };
  if (!elementUpdateStateCache.has(elementKey)) {
    elementUpdateStateCache.set(elementKey, elementState);
  }
  const accessor = integrationApp ? accessorGenerator(integrationApp) : void 0;
  const swrKey = accessor && selector ? elementKey : void 0;
  const {
    data: item,
    mutate,
    error,
    isLoading,
    isValidating
  } = useSWR(swrKey, () => accessor?.get(), {
    // pause revalidation if update is in progress to not overwrite local changes
    isPaused: () => elementState.updatedLocally || elementState.savingToServer
  });
  const loading = isLoading;
  const refreshing = isValidating;
  async function refresh() {
    return await mutate();
  }
  async function put(data, options = {
    revalidate: false
  }) {
    if (!accessor?.put) {
      throw new Error(
        /* FIXME: strictNullCheck temporary fix */
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        `PUT method is not supported for accessor ${accessor.constructor.name}`
      );
    }
    elementState.updatedLocally = true;
    const newLocalData = {
      ...item,
      ...data
    };
    const { optimisticData, ...remainingOptions } = options;
    await mutate(optimisticData || newLocalData, remainingOptions);
    await elementState.debouncedPut(data);
  }
  async function patch(data, options) {
    const newData = {
      ...item,
      ...data
    };
    return put(newData, options);
  }
  async function archive() {
    if (!accessor?.archive) {
      return;
    }
    await mutate({ ...item, archivedAt: (/* @__PURE__ */ new Date()).toISOString() }, false);
    await accessor?.archive();
    await mutate();
  }
  async function create(data) {
    if (!accessor?.create) {
      throw new Error(`"create method is not supported for accessor ${accessor?.constructor.name}`);
    }
    const result = await accessor?.create(data);
    return await mutate(result);
  }
  return {
    accessor,
    item,
    loading,
    saving: elementState.updatedLocally || elementState.savingToServer,
    error,
    refresh,
    refreshing,
    create,
    patch,
    put,
    archive
  };
}

export { useElement };
